/**
 * usePositioningAnalysis - Hook personnalisé pour gérer l’analyse IA de positionnement d’un projet.
 *
 * Permet de charger, sauvegarder et accéder aux résultats d’analyse IA, avec gestion du chargement et des erreurs.
 *
 * @param {string} slug - Le slug du projet
 * @returns {Object} {
 *   iaData: {Object|null}, // Résultat de l’analyse IA
 *   loading: {boolean}, // true si en cours de chargement ou sauvegarde
 *   error: {string|null}, // message d’erreur éventuel
 *   saveSuccess: {boolean}, // true si la sauvegarde a réussi
 *   fetchAnalysis: {Function}, // charge la dernière analyse IA
 *   saveAnalysis: {Function}, // sauvegarde une nouvelle analyse IA
 * }
 *
 * @example
 * const { iaData, loading, error, saveSuccess, fetchAnalysis, saveAnalysis } = usePositioningAnalysis(slug);
 * useEffect(() => { fetchAnalysis(); }, [slug]);
 */
import { useState, useCallback } from 'react';
import { getPositioningAnalyses, createPositioningAnalysis } from '../api';

export default function usePositioningAnalysis(slug) {
  const [iaData, setIaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fetchAnalysis = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getPositioningAnalyses(slug);
      setIaData(Array.isArray(data) && data.length ? data[data.length - 1] : data || null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erreur lors du chargement de l\'analyse');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const saveAnalysis = useCallback(async (payload) => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const data = await createPositioningAnalysis(slug, payload);
      setIaData(data);
      setSaveSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  return { iaData, loading, error, saveSuccess, fetchAnalysis, saveAnalysis };
} 